import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { DataImport } from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import FieldNames from '@lib/enums/fieldNames/importFormFieldNames';
import {
  getAttachmentValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import FileUploader from '@lib/components/FilesUploader/FileUploader';
import { FilesPreview } from '@lib/components/FilesUploader/enums';
import {
  fileSizeLimit,
  FileSizeLimits,
  UppyDataImportAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import { getDataImportKindOptionsByPermission } from '@lib/utils/dataImport';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Typography from '@lib/components/Typography/Typography';
import styles from '../ImportForm.module.scss';

type Args = {
  activeImport: DataImport | null | undefined;
  isLoading?: boolean;
  onShowDataImportErrors: (item?: DataImport) => void;
  t: TFunction<'translation', undefined>;
  isSitesAvailable: boolean;
  isAssetsAvailable: boolean;
};

export default function useFormFields(args: Args): FieldItem[] {
  const {
    activeImport,
    isLoading,
    onShowDataImportErrors,
    t,
    isSitesAvailable,
    isAssetsAvailable,
  } = args;
  const typeOptions = getDataImportKindOptionsByPermission({
    isSitesAvailable,
    isAssetsAvailable,
  });
  return [
    {
      name: FieldNames.Title,
      customRender: () => (
        <Typography variant="h3" key="import-data">
          {t('import-data')}
        </Typography>
      ),
      hideErrors: true,
      formItemClassName: styles.header,
    },
    {
      name: FieldNames.FileUpload,
      label: '',
      validation: getAttachmentValidation({ t, required: true }),
      element: FileUploader,
      hideErrorsSpace: true,
      componentProps: {
        showOfflineMessage: true,
        filesView: FilesPreview.Documents,
        allowedFileTypes: UppyDataImportAllowedFileTypes,
        note: t('data-import-upload-validation-msg', {
          size: FileSizeLimits.File,
        }),
        maxFileSize: fileSizeLimit,
        uploadButtonProps: {
          buttonSize: ButtonSizes.large,
          leftIcon: 'upload',
          fullWidth: true,
        },
        useReuploadButton: !!activeImport?.id,
      },
    },
    {
      name: FieldNames.FileUploadErrors,
      element: SupportiveMessage,
      getComponentProps: (values: Values) => {
        const isErrorsIgnored = _get(values, FieldNames.IgnoreErrors);
        return {
          text: t('validation-errors-detected'),
          type: SupportiveMessageType.FilledWithLeftBorder,
          color: isErrorsIgnored
            ? SupportiveMessageColor.Yellow
            : SupportiveMessageColor.Red,
          actions: (
            <Button
              buttonText={t('show-errors')}
              buttonType={
                isErrorsIgnored
                  ? ButtonTypes.primaryFilled
                  : ButtonTypes.negativeFilled
              }
              buttonSize={ButtonSizes.small}
              onClick={
                activeImport
                  ? () => onShowDataImportErrors(activeImport)
                  : undefined
              }
            />
          ),
        };
      },
      isVisibleFn: (values: Values) =>
        !isLoading &&
        !_get(values, [FieldNames.FileUpload, '_destroy']) &&
        activeImport?.failureDetails &&
        Object.keys(activeImport.failureDetails).length > 0,
    },
    {
      name: FieldNames.IgnoreErrors,
      label: t('ignore-rows-with-errors'),
      element: CheckBoxSingleBool,
      componentProps: {
        options: [{ label: 'checked', value: 'checked' }],
        rightLabelIcon: (
          <Tooltip body={t('import-ignore-errors-desc')}>
            <MaterialIcon icon="info" size="md-16" symbolsOutlined />
          </Tooltip>
        ),
      },
    },
    {
      name: FieldNames.Type,
      label: t('choose-import-type'),
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: typeOptions,
        RadioButtonComponent: RadioButtonAsButton,
        radioButtonComponentProps: {
          fullWidth: true,
          buttonSize: ButtonSizes.small,
        },
        wrapView: RadioButtonWrapViews.row,
        disabled: !!activeImport?.id,
      },
      isVisibleFn: () => typeOptions?.length > 1,
    },
  ];
}
